import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  IconButton,
  Modal,
  TextField,
  Alert,
  alpha,
  useTheme,
  Paper,
  Fade,
  Tooltip,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import {
  AddCircle as PlusCircle,
  CloudUpload as Upload,
  Delete as Trash,
  Close as CloseIcon,
} from "@mui/icons-material";
import AddphotoSvg from "../../Images/Addphoto.svg";

const GOLD = "#D4AF37";

const Photo = () => {
  const theme = useTheme();
  const [photos, setPhotos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [photoTitle, setPhotoTitle] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [showAlert, setShowAlert] = useState(false);

  const openModal = () => setShowModal(true);
  const closeModal = () => {
    setShowModal(false);
    setPhotoTitle("");
    setUploadedFile(null);
    setPreviewFile(null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "image/*": [] },
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setUploadedFile(file);
        setPreviewFile(URL.createObjectURL(file));
      }
    },
  });

  const handleAddPhoto = () => {
    if (photoTitle && uploadedFile) {
      const newPhoto = {
        id: Date.now(),
        title: photoTitle,
        file: URL.createObjectURL(uploadedFile),
      };
      setPhotos([newPhoto, ...photos]);
      setShowAlert(true);
      closeModal();
      setTimeout(() => setShowAlert(false), 3000);
    } else {
      alert("Please provide a title and upload a photo.");
    }
  };

  const handleDeletePhoto = (id) => {
    setPhotos(photos.filter((photo) => photo.id !== id));
  };

  return (
    <Box sx={{ p: 1 }}>
      {/* Success Alert */}
      <Fade in={showAlert}>
        <Alert
          severity="success"
          sx={{ mb: 3, borderRadius: '12px', bgcolor: alpha('#4caf50', 0.1), color: '#2e7d32', fontWeight: 600, display: showAlert ? 'flex' : 'none' }}
          onClose={() => setShowAlert(false)}
        >
          Photo added successfully!
        </Alert>
      </Fade>

      {/* Action Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
         <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: "'Outfit', sans-serif" }}>
           Photos Gallery
         </Typography>
         <Button
           variant="contained"
           onClick={openModal}
           startIcon={<PlusCircle />}
           sx={{
             bgcolor: GOLD,
             color: '#000',
             fontWeight: 700,
             px: 3,
             py: 1.2,
             '&:hover': { bgcolor: '#B8860B' }
           }}
         >
           Add Photo
         </Button>
      </Box>

      {/* Grid Content */}
      {photos.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10, bgcolor: alpha(GOLD, 0.02), borderRadius: 6, border: `2px dashed ${alpha(GOLD, 0.1)}` }}>
          <img src={AddphotoSvg} alt="Add Photos" style={{ width: 120, opacity: 0.6, marginBottom: 24 }} />
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>No Photos Found</Typography>
          <Typography variant="body2" color="text.secondary">Start adding your memories by clicking the button above.</Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {photos.map((photo) => (
            <Grid item key={photo.id} xs={12} sm={6} md={4}>
              <Card sx={{ 
                borderRadius: 4, 
                overflow: 'hidden', 
                position: 'relative',
                border: `1px solid ${alpha(GOLD, 0.1)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: GOLD,
                  transform: 'translateY(-5px)',
                  '& .delete-btn': { opacity: 1 }
                }
              }}>
                <Tooltip title="Delete Photo">
                  <IconButton
                    className="delete-btn"
                    onClick={() => handleDeletePhoto(photo.id)}
                    sx={{ 
                      position: 'absolute', top: 10, right: 10, 
                      bgcolor: 'rgba(244, 67, 54, 0.9)', color: 'white', 
                      zIndex: 1, opacity: { xs: 1, md: 0 }, 
                      transition: 'opacity 0.2s',
                      '&:hover': { bgcolor: '#d32f2f' } 
                    }}
                    size="small"
                  >
                    <Trash fontSize="small" />
                  </IconButton>
                </Tooltip>

                <CardMedia
                  component="img"
                  height="200"
                  image={photo.file}
                  alt={photo.title}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, noWrap: true }}>{photo.title}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Upload Modal */}
      <Modal open={showModal} onClose={closeModal} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Paper sx={{ width: '100%', maxWidth: 500, borderRadius: 4, overflow: 'hidden', outline: 'none' }}>
           <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
             <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: "'Outfit', sans-serif" }}>Upload Photo</Typography>
             <IconButton onClick={closeModal} size="small"><CloseIcon /></IconButton>
           </Box>

           <Box sx={{ p: 3 }}>
              <TextField 
                fullWidth 
                placeholder="Enter photo title" 
                value={photoTitle}
                onChange={(e) => setPhotoTitle(e.target.value)}
                sx={{ mb: 3 }}
              />

              <Box 
                {...getRootProps()}
                sx={{ 
                  border: `2px dashed ${isDragActive ? GOLD : alpha(theme.palette.divider, 0.4)}`,
                  borderRadius: 3,
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: isDragActive ? alpha(GOLD, 0.05) : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: alpha(GOLD, 0.02), borderColor: GOLD }
                }}
              >
                <input {...getInputProps()} />
                {previewFile ? (
                  <img src={previewFile} alt="Preview" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                ) : (
                  <Box>
                    <Upload sx={{ fontSize: 40, color: GOLD, mb: 1, opacity: 0.8 }} />
                    <Typography variant="body2" color="text.secondary">
                      {isDragActive ? "Drop the image here..." : "Drag & drop an image here, or click to select"}
                    </Typography>
                  </Box>
                )}
              </Box>
           </Box>

           <Box sx={{ p: 3, bgcolor: alpha(theme.palette.background.default, 0.5), display: 'flex', gap: 2 }}>
              <Button fullWidth variant="outlined" onClick={closeModal} sx={{ borderRadius: 3 }}>Cancel</Button>
              <Button 
                fullWidth 
                variant="contained" 
                onClick={handleAddPhoto} 
                sx={{ borderRadius: 3, bgcolor: GOLD, color: '#000', '&:hover': { bgcolor: '#B8860B' } }}
              >
                Add Photo
              </Button>
           </Box>
        </Paper>
      </Modal>
    </Box>
  );
};

export default Photo;
